import React, { useState, useEffect } from 'react';
import { SimpleListOfEmailsProps } from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import EmailListView, { EmailListItem } from '../../../common/EmailListView';
import EmailComponent from './EmailComponent';
import api from '../../../../api';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';

import '../../../common/email-list-view.scss';

interface SimpleListOfEmailsComponentProps extends SimpleListOfEmailsProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

interface EmailViewState {
  mode: 'list' | 'loading' | 'email';
  selectedEmailId?: string;
  error?: string;
}

interface EmailData {
  id: string;
  from: string;
  to: string;
  subject: string;
  snippet: string;
  date: string;
  labels: string[];
}

const SimpleListOfEmails: React.FC<SimpleListOfEmailsComponentProps> = ({
  emailIds,
  title,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [emails, setEmails] = useState<EmailData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewState, setViewState] = useState<EmailViewState>({ mode: 'list' });

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const response = await api.post<{
          success: boolean;
          emails: EmailData[];
        }>(
          '/agent-email-storage/by-ids',
          { emailIds },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setEmails(response.data.emails);
      } catch (err: any) {
        console.error('Error fetching emails:', err);
        setError(err.response?.data?.error || 'Failed to fetch emails');
      } finally {
        setLoading(false);
      }
    };

    if (emailIds && emailIds.length > 0) {
      fetchEmails();
    } else {
      setLoading(false);
    }
  }, [emailIds, getAccessTokenSilently]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';
    try {
      const emailDate = new Date(dateString);

      if (isToday(emailDate)) {
        return formatDistanceToNow(emailDate, { addSuffix: true });
      }

      if (isYesterday(emailDate)) {
        return `Yesterday ${format(emailDate, 'HH:mm')}`;
      }

      if (isThisWeek(emailDate)) {
        return format(emailDate, 'EEE HH:mm');
      }

      return format(emailDate, 'HH:mm:ss, EEE, d MMM');
    } catch {
      return dateString;
    }
  };

  const getPillForEmail = (email: EmailData) => {
    if (email.labels?.includes('SENT')) {
      return { text: 'Sent', variant: 'green' as const };
    }
    if (email.labels?.includes('DRAFT')) {
      return { text: 'Draft', variant: 'default' as const };
    }
    if (email.labels?.includes('UNREAD')) {
      return { text: 'Unread', variant: 'blue' as const };
    }
    return { text: '', variant: 'default' as const };
  };

  const handleEmailClick = (item: EmailListItem) => {
    setViewState({ mode: 'email', selectedEmailId: item.id });
  };

  const handleBackToList = () => {
    setViewState({ mode: 'list' });
  };

  // Show individual email view
  if (viewState.mode === 'email' && viewState.selectedEmailId) {
    return (
      <div className='email-component-wrapper'>
        <div className='email-detail-header'>
          <button
            className='back-to-list-button'
            onClick={handleBackToList}
            type='button'
          >
            ← Back to List
          </button>
        </div>
        <div className='lower-wrapper'>
          <EmailComponent
            emailId={viewState.selectedEmailId}
            threadId={threadId || ''}
            updateThreadViewVisualDescription={
              updateThreadViewVisualDescription
            }
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='simple-list-of-emails loading-view'>
        <div className='placeholder-email'>
          <div className='placeholder-subject'></div>
          <div className='placeholder-body'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='simple-list-of-emails'>
        <p className='error-message'>Error: {error}</p>
      </div>
    );
  }

  // Transform emails to EmailListItem format
  const emailListItems: EmailListItem[] = emails.map((email) => ({
    id: email.id,
    pill: getPillForEmail(email),
    address: email.from || 'Unknown',
    date: formatDate(email.date),
    subject: email.subject || '(No Subject)',
    snippet: email.snippet || '',
  }));

  if (emailListItems.length === 0) {
    return (
      <div className='simple-list-of-emails'>
        <p>No emails found.</p>
      </div>
    );
  }

  return (
    <div className='simple-list-of-emails'>
      <EmailListView
        items={emailListItems}
        showPills={true}
        emptyHeader='No emails'
        emptyText='No emails to display'
        onItemClick={handleEmailClick}
      />
    </div>
  );
};

export default SimpleListOfEmails;
